//may7 code without smart check for existing file
// src/utils/frontend_assetManager.js.

import { app, db, storage, functions } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { arrayUnion, onSnapshot} from 'firebase/firestore';
import { safeGetItem, safeSetItem } from '../utils/storageUtils';

// INSERT (helper utilities)
const _nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const _logTimer = (label, t0, extra = undefined) => {
  const t1 = _nowMs();
  const ms = Math.round(t1 - t0);
  if (extra !== undefined) {
    console.log(label, ms, extra);
  } else {
    console.log(label, ms);
  }
  return t1;
};

const _fetchWithTimeout = async (url, opts = {}, timeoutMs = 60000) => {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...opts, signal: controller.signal });
  } finally {
    clearTimeout(t);
  }
};

/* ────────────────────────────────────────────────────────────────
 * ENTERPRISE: Environment-aware HTTP Cloud Function URL builder
 * ----------------------------------------------------------------
 * WHY
 *  • The app is deployed to separate Firebase projects for staging/prod.
 *  • Hardcoded production URLs make staging call production Functions.
 *  • Firebase app config already knows the active projectId.
 *
 * CONTRACT
 *  • Works in CRA using REACT_APP_FIREBASE_PROJECT_ID.
 *  • Falls back to initialized Firebase app options.
 *  • Keeps the region explicit and centralized.
 *  • Throws loudly if no projectId is available, so we do not silently
 *    call the wrong backend.
 * ──────────────────────────────────────────────────────────────── */
const DEFAULT_FUNCTIONS_REGION = 'us-central1';

const getHttpFunctionUrl = (functionName, region = DEFAULT_FUNCTIONS_REGION) => {
  const name = String(functionName || '').trim();

  if (!name) {
    throw new Error('[frontend_assetManager] functionName is required.');
  }

  const projectId =
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    app?.options?.projectId ||
    '';

  if (!projectId) {
    throw new Error(
      '[frontend_assetManager] Firebase projectId is missing. ' +
      'Check REACT_APP_FIREBASE_PROJECT_ID / Firebase app config.'
    );
  }

  return `https://${region}-${projectId}.cloudfunctions.net/${name}`;
};

/* ────────────────────────────────────────────────────────────────
 * ENTERPRISE: Environment-aware Firebase Storage URL builder
 * ----------------------------------------------------------------
 * WHY
 *  • Staging and production use separate Firebase projects/buckets.
 *  • Hardcoded bucket names cause staging UI to render production URLs.
 *  • Storage bucket identity should come from the active Firebase config,
 *    not from component-level constants.
 *
 * CONTRACT
 *  • Works in CRA using REACT_APP_FIREBASE_STORAGE_BUCKET.
 *  • Falls back to initialized Firebase app/storage config.
 *  • Never falls back to a hardcoded production bucket.
 *  • Returns null instead of guessing if bucket/path is unavailable.
 * ──────────────────────────────────────────────────────────────── */
export const getConfiguredStorageBucketName = () => {
  const rawBucket =
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    storage?.app?.options?.storageBucket ||
    app?.options?.storageBucket ||
    '';

  const bucket = String(rawBucket || '')
    .trim()
    .replace(/^gs:\/\//i, '')
    .replace(/\/+$/g, '');

  if (!bucket) {
    console.warn(
      '[frontend_assetManager] Firebase storage bucket is missing. ' +
      'Check REACT_APP_FIREBASE_STORAGE_BUCKET / Firebase app config.'
    );
    return '';
  }

  return bucket;
};

export const buildStoragePublicUrl = (objectPath) => {
  if (!objectPath) return null;

  const path = String(objectPath || '').trim();
  if (!path) return null;

  // Already browser-usable; return unchanged.
  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('data:') ||
    path.startsWith('blob:')
  ) {
    return path;
  }

  const bucket = getConfiguredStorageBucketName();
  if (!bucket) return null;

  const safePath = path
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');

  return `https://storage.googleapis.com/${bucket}/${safePath}`;
};

 // In-memory cache: orgId -> default collectionId
 const DEFAULT_FOLDER_CACHE = new Map();
 const defaultFolderKey = (orgId) => `org_${orgId}_defaultCollectionId`;

// ────────────────────────────────────────────────────────────────
// ENTERPRISE: In-flight request de-dupe for getAssetById (fetchAssetById)
// - Prevents duplicate concurrent HTTP calls for the same asset+options
// - Does NOT change auth model, does NOT persist across sessions
// - Ensures UI layers (AssetCard / AssetLibrary / useAssetPreview) share one request
// Map<key, Promise<any>>
// ────────────────────────────────────────────────────────────────
const INFLIGHT_FETCH_ASSET_BY_ID = new Map();

/**
 * Ensures a default folder exists for an organization.
 * Uses hybrid cache:
 *   1) In-memory (fastest)
 *   2) session/local storage via storageUtils (survives reload)
 * Falls back to Firestore exactly once per miss and then caches.
 * @param {string} organizationId
 * @param {string} userId
 * @returns {Promise<string>} default collectionId
 */
const ensureDefaultFolder = async (organizationId, userId) => {
  try {
    if (!organizationId) throw new Error('organizationId required');

    // 1) In-memory cache
    const memHit = DEFAULT_FOLDER_CACHE.get(organizationId);
    if (memHit) {
      console.log('[ensureDefaultFolder] Cache hit (mem):', memHit);
      return memHit;
    }

    // 2) session/local storage cache
    const key = defaultFolderKey(organizationId);
    const storedId = safeGetItem(key);
    if (storedId) {
      console.log('[ensureDefaultFolder] Cache hit (storage):', storedId);
      DEFAULT_FOLDER_CACHE.set(organizationId, storedId);
      return storedId;
    }

    // 3) Firestore lookup
    const collections = await fetchCollections(organizationId, null);
    const defaultFolder = collections.find((c) => c.isDefault);

    if (!defaultFolder) {
      console.log(
        `[ensureDefaultFolder] No default folder for org ${organizationId}. Creating...`
      );
      const created = await createCollection(
        'Default Folder',
        organizationId,
        userId,
        null,
        true
      );
      console.log('[ensureDefaultFolder] Default folder created:', created);

      const newId = created?.id || created?.collectionId;
      if (!newId) throw new Error('Failed to resolve created default folder id');

      // cache in both layers
      DEFAULT_FOLDER_CACHE.set(organizationId, newId);
      safeSetItem(key, newId);
      return newId;
    }

    console.log('[ensureDefaultFolder] Default folder found:', defaultFolder);

    const id = defaultFolder.id || defaultFolder.collectionId;
    DEFAULT_FOLDER_CACHE.set(organizationId, id);
    safeSetItem(key, id);
    return id;
  } catch (error) {
    console.error('Error ensuring default folder:', error);
    throw error;
  }
};


/**
 * Saves asset metadata to Firestore.
 * @param {Object} metadata - The metadata object to save.
 */
const saveMetadataToFirestore = async (metadata) => {
  try {
    const assetId = uuidv4(); // Generate a unique asset ID
    const metadataWithId = {
      ...metadata,
      id: assetId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    console.log("[saveMetadataToFirestore] Saving metadata:", metadataWithId);

    // Save metadata to the "assets" collection
    await setDoc(doc(db, "assets", assetId), metadataWithId);

    console.log("[saveMetadataToFirestore] Metadata saved successfully:", metadataWithId);
    return metadataWithId;
  } catch (error) {
    console.error("[saveMetadataToFirestore] Error saving metadata to Firestore:", error);
    throw error;
  }
};


// ────────────────────────────────────────────────────────────────
// Safe blob‑to‑Base64 helper
// • Works for any file size without spreading every byte onto the stack
// • Returns *only* the Base‑64 payload (no data:URL prefix)
// ────────────────────────────────────────────────────────────────
const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () =>
      resolve(reader.result.split(',')[1]);           // strip “data:mime;base64,”
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * Uploads an asset using the backend HTTPS function to handle resizing,
 * metadata creation, and validation. Ensures a default folder exists
 * if no folder is specified.
 *
 * @param {File|Blob|string} file           File / Blob  *or*  base-64 string
 * @param {string}           organizationId
 * @param {string}           userId
 * @param {Object}           additionalMetadata
 * @param {string|null}      collectionId
 * @param {Array<string>}    userPermissions
 * @param {string|null}      type           e.g. "icon", "banner", "screenshot"
 * @param {string}           authToken
 * @param {string|null}      overrideName   force the file name (optional)
 * @returns {Object}         asset metadata from the backend
 */
export const uploadAssetWithDefaultFolder = async (
  file,
  organizationId,
  userId,
  additionalMetadata = {},
  collectionId = null,
  userPermissions,
  type = null,
  authToken,
  overrideName = null
) => {
  try {
    /* ------------------------------------------------------------------ */
    /* 0.  Basic guards & permission checks                               */
    /* ------------------------------------------------------------------ */

    // INSERT (flow timing start)
    const __t_flow0 = _nowMs();

    if (!authToken) {
      throw new Error("[uploadAssetWithDefaultFolder] Firebase authToken is undefined or null.");
    }

    if (!userPermissions || !Array.isArray(userPermissions)) {
      console.error("[uploadAssetWithDefaultFolder] Invalid permissions array:", userPermissions);
      throw new Error("Permissions data is invalid or missing.");
    }

    /* ------------------------------------------------------------------
     * Enterprise permission policy
     * ------------------------------------------------------------------
     * Full DAM uploads require uploadAsset.
     *
     * Base-plan form image assets are allowed to continue to the backend
     * for server-side validation:
     *   - icon
     *   - banner
     *   - screenshot
     *
     * WHY
     *  • Standard/base plans can use form image fields and screenshots.
     *  • Pro/Enterprise unlock DAM management, not basic image rendering.
     *  • Backend remains the source of truth and still validates org membership.
     * ------------------------------------------------------------------ */
    const BASE_FORM_IMAGE_TYPES = new Set(["icon", "banner", "screenshot"]);
    const normalizedUploadType =
      typeof type === "string" ?
        type.trim().toLowerCase().replace(/[\s_-]+/g, "") :
        null;

    const isBaseFormImageUpload =
      normalizedUploadType &&
      BASE_FORM_IMAGE_TYPES.has(normalizedUploadType);

    if (!userPermissions.includes("uploadAsset") && !isBaseFormImageUpload) {
      console.error("[uploadAssetWithDefaultFolder] Missing Permission: uploadAsset");
      throw new Error("You do not have permission to upload assets.");
    }

    if (!userPermissions.includes("uploadAsset") && isBaseFormImageUpload) {
      console.log("[uploadAssetWithDefaultFolder] Base form image upload allowed for backend validation:", {
        type,
        normalizedUploadType,
      });
    }

    /* ------------------------------------------------------------------ */
    /* 1.  Ensure a default folder exists                                 */
    /* ------------------------------------------------------------------ */
    console.log("[uploadAssetWithDefaultFolder] Ensuring default folder for organization:", organizationId);

    // (time default folder resolution)
    const __t_folder0 = _nowMs();

    collectionId = collectionId || (await ensureDefaultFolder(organizationId, userId));

    // (time log)
    _logTimer('[SUBMIT:TIMER][client] ensureDefaultFolder ms =', __t_folder0);

    console.log("[uploadAssetWithDefaultFolder] Using collectionId:", collectionId);
    /* ------------------------------------------------------------------ */
    /* 2. Decide “large file” vs “small file” path                        */
    /*    (Only valid for real File/Blob – strings skip this)             */
    /* ------------------------------------------------------------------ */
    const isBlob      = file instanceof Blob || file instanceof File;
    const blobSize    = isBlob ? file.size : 0;                 // safe for strings
    const isLargeFile = isBlob && blobSize > 512 * 1024;        // >512 KB

    /* ==================================================================
       ███  LARGE FILE  – signed-URL upload  ➜  BACKEND handles metadata
       ==================================================================
       CHANGE: After PUT to the signed URL, call the same Cloud Function
       used by the small-file path, but with `filePath` so the backend
       can generate all versions, validate dimensions, and write the
       assets/{assetId} doc consistently.
       ================================================================== */
    if (isLargeFile) {
      console.log(
        "[uploadAssetWithDefaultFolder] Large file detected. " +
        "Using signed URL upload…"
      );

      /* Get the signed URL (and final storage path) from Cloud Functions */
      const { uploadUrl, filePath } =
       await getSignedUploadUrl(
         overrideName || file.name,
         organizationId,
         authToken,
         file.type || "application/octet-stream" // pass MIME to server
       );


      /* PUT the binary to Cloud Storage */
      const __t_put0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());

      const uploadResponse = await _fetchWithTimeout(
        uploadUrl,
        {
          method : "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body   : file,
        },
        180000 //  enterprise safety: large uploads can be slow
      );

      const __t_put1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      console.log('[SUBMIT:TIMER][client] signed PUT ms =', Math.round(__t_put1 - __t_put0), {
        fileName: overrideName || file.name,
        bytes: blobSize,
      });
      
      if (!uploadResponse.ok) {
        console.error(
          "[uploadAssetWithDefaultFolder] Failed to upload via signed URL:",
          uploadResponse
        );
        throw new Error("Failed to upload file using signed URL.");
      }

      console.log(
        "[uploadAssetWithDefaultFolder] File uploaded via signed URL. " +
        "Handing off to backend for versioning + metadata write…"
      );

      /* Hand off to the backend uploader with filePath */
      const payload = {
        // NOTE: For large files we DO NOT send fileBuffer; we pass filePath.
        filePath,
        fileName: overrideName || file.name,
        fileType: file.type || "application/octet-stream", // optional but helpful
        fileSize: blobSize,                                 // optional
        organizationId,
        userId,
        additionalMetadata,  // includes formId/tags/etc. for backend to embed
        collectionId,
        type,                // 'icon' | 'banner' | 'screenshot' | null
      };

      console.log("[uploadAssetWithDefaultFolder] Prepared large-file payload for backend:", payload);

      // SUBMIT TIMER (client): /uploadAsset HTTP start
      const __t_uploadAsset0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());

      // (use timeout-safe fetch so the request never hangs indefinitely)
      const response = await _fetchWithTimeout(
        getHttpFunctionUrl("uploadAsset"),
        {
          method : "POST",
          headers: {
            Authorization : `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
        60000
      );

      console.log(
        "[uploadAssetWithDefaultFolder] Backend HTTP Response status (large-file):",
        response.status
      );

    const __t_uploadAsset1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    console.log('[SUBMIT:TIMER][client] uploadAsset HTTP ms =', Math.round(__t_uploadAsset1 - __t_uploadAsset0));

      // Accept both 202 (placeholder queued) and 200 (legacy success)
      let responseData;
      const contentType = response.headers.get("Content-Type") || "";
      if (contentType.includes("application/json")) {
        responseData = await response.json();
      } else {
        const txt = await response.text();
        try { responseData = JSON.parse(txt); } catch (_) { responseData = {}; }
      }

      if (response.status === 202) {
        // Background processing queued; server returned a placeholder doc.
        const placeholder = responseData.assetMetadata || responseData;
        if (!placeholder.id && placeholder.assetId) {
          placeholder.id = placeholder.assetId;
        }

        console.log(
          "[uploadAssetWithDefaultFolder] Placeholder received (processing):",
          placeholder
        );

        // Expose a helper: caller can await finalization when status flips.
        // We resolve when the asset doc status != 'processing'.
        placeholder.__waitForFinal = () =>
          new Promise((resolve) => {
            try {
              const unsub = onSnapshot(
                doc(db, "assets", placeholder.id),
                (snap) => {
                  const d = snap.exists() ? { id: snap.id, ...snap.data() } : null;
                  if (!d) return;
                  if (d.status && d.status !== "processing") {
                    unsub();
                    resolve(d);
                  }
                },
                (err) => {
                  console.warn(
                    "[uploadAssetWithDefaultFolder] onSnapshot error (non-fatal):",
                    err?.message || err
                  );
                  // Resolve with current placeholder if watch fails
                  resolve(placeholder);
                }
              );
            } catch (e) {
              console.warn(
                "[uploadAssetWithDefaultFolder] waitForFinal setup failed:",
                e?.message || e
              );
              resolve(placeholder);
            }
          });

        // INSERT (flow total)
        _logTimer('[SUBMIT:TIMER][client] uploadAssetWithDefaultFolder TOTAL ms =', __t_flow0, {
          mode: 'large',
          httpStatus: response.status,
          assetId: placeholder.id || placeholder.assetId || null,
          status: placeholder.status || 'processing',
        });

        // Return the placeholder now for optimistic UI.
        return placeholder;
      }

      // Legacy 200 OK path: full processing already done
      if (!response.ok) {
        let errorMessage =
          `Failed to upload asset (large-file). HTTP status: ${response.status}`;
        try {
          errorMessage =
            responseData?.error || responseData?.message || errorMessage;
        } catch (_) { /* ignore */ }

        console.error(
          "[uploadAssetWithDefaultFolder] Response error (large-file):",
          errorMessage
        );
        throw new Error(errorMessage);
      }

      console.log(
        "[uploadAssetWithDefaultFolder] Asset processed by backend (large-file):",
        responseData
      );

      const meta = responseData.assetMetadata || responseData;
      if (!meta.id && meta.assetId) meta.id = meta.assetId;

      // INSERT (flow total)
      _logTimer('[SUBMIT:TIMER][client] uploadAssetWithDefaultFolder TOTAL ms =', __t_flow0, {
        mode: 'large',
        httpStatus: response.status,
        assetId: meta.id || meta.assetId || null,
        status: meta.status || null,
      });

      return meta;

    }

    /* ==================================================================
       ███  SMALL FILE  – File/Blob  OR  base-64 string
       ================================================================== */
    console.log("[uploadAssetWithDefaultFolder] Small file. Using standard upload process...");

    let base64File, fileName, fileType, fileSize;

    /* ---------- 2-A. File / Blob path (no changes) -------------------- */
    if (isBlob) {
      console.log("[uploadAssetWithDefaultFolder] Reading File/Blob into memory...");
      base64File   = await blobToBase64(file);               // ← safe helper
      fileName     = overrideName || file.name;              // ← keep override behavior
      fileType     = file.type || "application/octet-stream";
      fileSize     = file.size;
    } else {
      /* ---------- 2-B. Base-64 STRING path (screenshots) -------------- */
      console.log("[uploadAssetWithDefaultFolder] Base-64 string detected – skipping decode/re-encode.");

      const sanitizedBase64 = file.replace(/[\r\n\s]+/g, "");   // strip whitespace ONLY
      base64File  = sanitizedBase64;
      fileName = overrideName
              || additionalMetadata?.fileName
              || "screenshot.jpeg";

      if (fileName.endsWith(".png"))                   fileType = "image/png";
      else if (fileName.endsWith(".jpg") ||
               fileName.endsWith(".jpeg"))             fileType = "image/jpeg";
      else                                             fileType = "image/webp";

      fileSize    = Math.floor(sanitizedBase64.length * 0.75); // rough byte length
    }

    /* ------------- debug preview ------------------------------------- */
    console.log("[uploadAssetWithDefaultFolder] Base64 preview:", base64File.slice(0, 50));
    console.log("[uploadAssetWithDefaultFolder] Base64 length :", base64File.length);

    /* ------------------------------------------------------------------ */
    /* 3.  Prepare payload & call backend Cloud Function                  */
    /* ------------------------------------------------------------------ */
    const payload = {
      fileBuffer: base64File,         // small-file path sends buffer
      fileName,
      fileType,
      fileSize,
      organizationId,
      userId,
      additionalMetadata,
      collectionId,
      type,
    };

    console.log("[uploadAssetWithDefaultFolder] Prepared payload (small-file):", payload);

    //  timer start for SMALL /uploadAsset HTTP
    const __t_uploadAssetSmall0 = _nowMs();

    // use timeout-safe fetch so small uploads never hang indefinitely
    const response = await _fetchWithTimeout(
      getHttpFunctionUrl("uploadAsset"),
      {
        method : "POST",
        headers: {
          Authorization : `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
      60000 // timeoutMs: 60s safety cutoff for small uploads
    );

    // timer end right after status is available
    _logTimer('[SUBMIT:TIMER][client] uploadAsset HTTP ms =', __t_uploadAssetSmall0, { mode: 'small', status: response.status });

    console.log("[uploadAssetWithDefaultFolder] HTTP Response status (small-file):", response.status);

    if (!response.ok) {
      const contentType = response.headers.get("Content-Type") || "";
      let errorMessage  = `Failed to upload asset. HTTP status: ${response.status}`;

      try {
        if (contentType.includes("application/json")) {
          const err = await response.json();
          errorMessage = err?.error || err?.message || errorMessage;
        } else {
          const errText = await response.text();
          if (errText) errorMessage = errText;
        }
      } catch (parseErr) {
        console.warn("[uploadAssetWithDefaultFolder] Failed to parse error response:", parseErr);
      }

      console.error("[uploadAssetWithDefaultFolder] Response error:", errorMessage);
      throw new Error(errorMessage);
    }

    const responseData = await response.json();
    console.log("[uploadAssetWithDefaultFolder] Asset uploaded successfully (small-file):", responseData);

    /* --------------------------------------------------------------
     * Normalise: guarantee the caller always receives `.id`
     * -------------------------------------------------------------- */
    const meta = responseData.assetMetadata || responseData;
    if (!meta.id && meta.assetId) meta.id = meta.assetId;

    // INSERT (flow total)
    _logTimer('[SUBMIT:TIMER][client] uploadAssetWithDefaultFolder TOTAL ms =', __t_flow0, {
      mode: 'small',
      httpStatus: response.status,
      assetId: meta.id || meta.assetId || null,
      status: meta.status || null,
    });

    return meta;

  } catch (error) {
    console.error("[uploadAssetWithDefaultFolder] Error during upload process:", error);
    throw error;
  }
};



// ────────────────────────────────────────────────────────────────
// Enterprise deletion policy:
// Always delete via Cloud Function so Storage + Firestore stay consistent.
// (Client-side deletion is only for dev rescue / admin scripts.)
// ────────────────────────────────────────────────────────────────
export const deleteAssetViaCloudFunction = async (
  assetId,
  authToken
) => {
  if (!assetId) throw new Error('[deleteAssetViaCloudFunction] assetId is required.');
  if (!authToken) throw new Error('[deleteAssetViaCloudFunction] authToken is required.');

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);

  try {
    const res = await fetch(
      getHttpFunctionUrl('deleteAsset'),
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assetId }),
        signal: controller.signal,
      }
    );

    if (!res.ok) {
      let serverMsg = '';
      try {
        const data = await res.json();
        serverMsg = data?.error || data?.message || '';
      } catch {
        serverMsg = await res.text().catch(() => '');
      }
      throw new Error(
        `[deleteAssetViaCloudFunction] Delete failed (${res.status})${serverMsg ? ': ' + serverMsg : ''}`
      );
    }

    return await res.json().catch(() => ({ ok: true }));
  } finally {
    clearTimeout(timeout);
  }
};



/**
 * Deletes an asset from Firebase Storage and removes its metadata from Firestore.
 * Validates user permissions before deletion.
 * @param {string} assetId - The ID of the asset to delete.
 * @param {Array<string>} userPermissions - List of user's permissions.
 */
export const deleteAsset = async (assetId, userPermissions) => {
  try {
    // Enterprise safety rail:
    // In production, deletion must go through the Cloud Function.
    // This client helper is for DEV/admin recovery only.
    if (process.env.NODE_ENV === 'production') {
      throw new Error(
        '[deleteAsset] Client-side delete is disabled in production. ' +
        'Use deleteAssetViaCloudFunction(assetId, authToken).'
      );
    }

    if (!userPermissions.includes('deleteAsset')) {
      throw new Error('You do not have permission to delete assets.');
    }

    const assetRef = doc(db, 'assets', assetId);
    const assetSnapshot = await getDoc(assetRef);

    if (!assetSnapshot.exists()) {
      throw new Error(`Asset with ID ${assetId} does not exist.`);
    }

    const assetData = assetSnapshot.data();
//    const storageRef = ref(storage, `assets/${assetData.organizationId}/${assetId}/${assetData.name}`);

//    await deleteObject(storageRef);

    // NEW (enterprise-consistent): delete every Storage object the backend recorded
    // - versions.*.path (original/thumbnail/icon2x/etc.)
    // - thumb.urls["400"/"800"] and thumb.micro["32"/"64"] (new unified thumb contract)
    // - De-dupe paths and skip non-string nodes (e.g. versions["<v>"].responses snapshots)
    const paths = new Set();

    // 1) versions.*.path
    Object.values(assetData.versions || {}).forEach((v) => {
      const p = v && typeof v === 'object' ? v.path : null;
      if (typeof p === 'string' && p.trim()) paths.add(p.trim());
    });

    // 2) thumb.urls + thumb.micro
    const thumb = assetData.thumb && typeof assetData.thumb === 'object' ? assetData.thumb : {};
    const urls  = thumb.urls  && typeof thumb.urls  === 'object' ? thumb.urls  : {};
    const micro = thumb.micro && typeof thumb.micro === 'object' ? thumb.micro : {};

    Object.values(urls).forEach((p) => {
      if (typeof p === 'string' && p.trim()) paths.add(p.trim());
    });
    Object.values(micro).forEach((p) => {
      if (typeof p === 'string' && p.trim()) paths.add(p.trim());
    });

    const deletePromises = Array.from(paths).map((p) =>
      deleteObject(ref(storage, p)).catch((e) => {
        // Non-fatal: object may already be gone or permissions differ
        console.warn('[deleteAsset] Skipping file delete (non-fatal):', p, e?.message || e);
      })
    );

    await Promise.allSettled(deletePromises);
    await deleteDoc(assetRef);

    console.log(`Asset ${assetId} deleted successfully.`);

  } catch (error) {
    console.error('Error deleting asset:', error);
    throw error;
  }
};

/**
 * Fetches assets using the Firebase Cloud Function.
 * @param {string} organizationId - Organization ID.
 * @param {string|null} collectionId - Optional collection ID for filtering.
 * @param {Array<string>} tags - Optional tags for filtering.
 * @param {string|null} searchQuery - Optional search query.
 * @param {number} pageSize - Number of items per page (clamped to 1..50).
 * @param {string|null} lastVisibleId - ID string from previous page (cursor).
 * @param {string} authToken - Firebase authentication token for the user.
 * @param {string|null} formIdFilter - Optional form-scope filter.
 * @param {string|null} assetType - Optional asset type filter
 *   (e.g., 'icon' | 'banner' | 'screenshot').
 * @param {boolean} includeUrls - Whether backend should attach signed
 *   thumbnail URLs (thumbUrl). Defaults to true.
 * @param {boolean} includeCount - Whether backend should compute totalCount.
 *   Defaults to false for faster first paint.
 * @param {Object|null} promotedFilters
 *   AUF fast filters payload:
 *   {
 *     equals?:    { [ufi: string]: string },
 *     equalsAny?: { [ufi: string]: string[] }, // OR on one UFI (backend maps to 'in')
 *     arrayAny?:  { [ufi: string]: string[] }, // array-contains-any
 *     dateRange?: { [ufi: string]: { start?: string|number|Date, end?: string|number|Date } }
 *   }
 * @returns {Object} Result containing { assets, lastVisibleId, totalCount }.
 */
export const fetchAssets = async (
  organizationId,
  collectionId = null,
  tags = [],
  searchQuery = '',
  pageSize = 12,
  lastVisibleId = null,
  authToken,
  formIdFilter = null,
  assetType = null,
  includeUrls = true,
  includeCount = false,
  promotedFilters = null           // (thread AUF fast filters)
) => {
  try {
    // Clamp page size to keep requests snappy and costs predictable.
    // Feel free to adjust MAX_PAGE down/up as your UX needs.
    const MAX_PAGE = 50;
    const effectivePageSize = Math.max(
      1,
      Math.min(Number(pageSize) || 12, MAX_PAGE)
    );
    console.log('[fetchAssets] Fetching assets from backend with:', {
      organizationId,
      collectionId,
      tags,
      searchQuery,
      pageSizeRequested: pageSize,
      pageSizeEffective: effectivePageSize,
      lastVisibleId,
      formIdFilter,
      assetType,
      includeUrls,
      includeCount,
      // keep log compact (lengths instead of raw arrays)
      promotedFilters:
        promotedFilters
          ? {
              equals   : Object.keys(promotedFilters.equals    || {}).length,
              equalsAny: Object.keys(promotedFilters.equalsAny || {}).length,
              arrayAny : Object.keys(promotedFilters.arrayAny  || {}).length,
              dateRange: Object.keys(promotedFilters.dateRange || {}).length,
            }
          : null,
    });

    // Validate the provided authToken
    if (!authToken) {
      console.error(
        '[fetchAssets] Missing authToken. Ensure the user is authenticated.'
      );
      throw new Error(
        'Missing authentication token. Please sign in and try again.'
      );
    }

    // --- new: sanitise the *ID string* we received --------------------
    const cursorId =
      typeof lastVisibleId === 'string' && lastVisibleId.trim() !== ''
        ? lastVisibleId.trim()
        : null;

    // ─────────────────────────────────────────────────────────────
    // NEW: compact promotedFilters to drop empty buckets
    // (keeps payload lean; backend happily accepts null)
    // ─────────────────────────────────────────────────────────────
    const compactPf =
      promotedFilters && typeof promotedFilters === 'object'
        ? Object.fromEntries(
            Object.entries(promotedFilters).filter(([, v]) =>
              v && typeof v === 'object' && Object.keys(v).length > 0
            )
          )
        : null;

 console.log('[fetchAssets] compactPf before request:', compactPf);

    // ✅ INSERT: timer start for fetchAssets HTTP
    const __t_fetchAssets0 = _nowMs();

    // Send the request to the backend function using a POST request with the authToken
    const response = await _fetchWithTimeout(
      getHttpFunctionUrl('fetchAssets'),
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`, // Pass the token explicitly
          'Content-Type': 'application/json',   // Set the content type
        },
        body: JSON.stringify({
          organizationId,
          collectionId,
          tags,
          searchQuery,
          pageSize: effectivePageSize,
          lastVisibleId: cursorId, //  send just the ID
          formIdFilter,
          assetType,               //  optional pass-through
          includeUrls,             //  optional pass-through
          includeCount,            //  ask backend to compute totalCount or not
          // ▼ NEW: send compacted filters (null when all buckets were empty)
          promotedFilters:
            compactPf && Object.keys(compactPf).length ? compactPf : null,
        }),
      },
      30000 // timeoutMs: 30s safety cutoff for list fetches
    );


    // Log the response status for debugging
    console.log(
      '[fetchAssets] Backend HTTP response status:',
      response.status
    );

    // timing log right after status
    _logTimer('[TIMER][client] fetchAssets HTTP ms =', __t_fetchAssets0, { status: response.status });

    // Check for HTTP errors and handle them gracefully
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[fetchAssets] Response error details:', errorText);
      throw new Error(
        `Failed to fetch assets. HTTP status: ${response.status}`
      );
    }

    // Parse and log the response JSON data
    const responseData = await response.json();
    console.log(
      '[fetchAssets] Successfully fetched assets from backend:',
      responseData
    );

    // Ensure the response contains the expected data structure
    if (!responseData || !Array.isArray(responseData.assets)) {
      console.error(
        '[fetchAssets] Invalid response format from backend:',
        responseData
      );
      throw new Error('Invalid response format from backend.');
    }

    // Normalize totalCount when backend is asked to skip it
    if (typeof responseData.totalCount !== 'number') {
      // Use page length as a cheap stand-in (cursor drives paging anyway)
      responseData.totalCount = responseData.assets.length;
    }

    // Return the response data to the caller
    return responseData; // Ensure this format matches the backend's output
  } catch (error) {
    // Log any errors encountered during the process
    console.error('[fetchAssets] Error during asset fetch:', error.message);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Get a single asset by id with a short-lived preview URL.
// Accepts a 'prefer' hint so the server can pick micro 32/64 thumbnails,
// used by the table view only. Other callers can omit this.
// ---------------------------------------------------------------------------
export const fetchAssetById = async (
  assetId,
  authToken,
  {
    includeUrls = true,
    dpr =
      (typeof window !== "undefined" ? window.devicePixelRatio : 1) || 1,
    includeMeta = false,
    prefer = null, // 'micro' | null (table view passes 'micro')
  } = {},
  fetchOpts = {} // optional fetch options (signal, etc.) — backwards compatible
) => {
  try {
    if (!assetId) {
      throw new Error("[fetchAssetById] assetId is required.");
    }
    if (!authToken) {
      throw new Error("[fetchAssetById] Missing authToken.");
    }

    // stable inflight key + de-dupe concurrent identical calls
    const dprKey = String(Math.round(Number(dpr || 1) * 10) / 10);
    const key = [
      String(assetId).trim(),
      includeUrls ? "1" : "0",
      includeMeta ? "1" : "0",
      dprKey,
      String(prefer || ""),
    ].join("|");

    const inflight = INFLIGHT_FETCH_ASSET_BY_ID.get(key);
    if (inflight) {
      return await inflight;
    }

    // create one shared promise for this request
    const p = (async () => {

      // (submit-path probe): who is calling fetchAssetById?
      try {
        if (typeof window !== "undefined" && window.__DEBUG_FETCH_ASSET_BY_ID__ === true) {
          console.log("[fetchAssetById][CALLSTACK]", {
            assetId,
            includeUrls,
            includeMeta,
            prefer,
          });
          console.log(new Error("[fetchAssetById] callsite stack").stack);
        }
      } catch (_) {}

      console.log("[fetchAssetById] Request:", {
        assetId,
        includeUrls,
        dpr,
        includeMeta,
        prefer, // log the hint for debugging
      });

      const res = await fetch(
        getHttpFunctionUrl("getAssetById"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            assetId,
            includeUrls,
            dpr,
            includeMeta,
            prefer, // forward hint to Cloud Function
          }),

          // allow AbortController signal when provided by callers (e.g. hooks)
          // ...(fetchOpts && typeof fetchOpts === "object" ? fetchOpts : {}),

          // allowlist only safe fetch options so callers can't
          // accidentally override method/headers/body and break auth.
          ...(fetchOpts && typeof fetchOpts === "object"
            ? {
                ...(fetchOpts.signal ? { signal: fetchOpts.signal } : {}),
                ...(typeof fetchOpts.keepalive === "boolean"
                  ? { keepalive: fetchOpts.keepalive }
                  : {}),
              }
            : {}),
        }
      );

      console.log("[fetchAssetById] HTTP status:", res.status);

      if (!res.ok) {
        const errText = await res.text();
        console.error("[fetchAssetById] Response error:", errText);
        throw new Error(
          `[fetchAssetById] Failed. HTTP status: ${res.status}`
        );
      }

      const data = await res.json();
      console.log("[fetchAssetById] Response:", data);
      // shape: { assetId, name, type, aspect, previewUrl?, meta? }
      return data;

    })();

    INFLIGHT_FETCH_ASSET_BY_ID.set(key, p);

    // ensure cleanup regardless of success/failure
    try {
      return await p;
    } finally {
      INFLIGHT_FETCH_ASSET_BY_ID.delete(key);
    }

  } catch (err) {
    console.error("[fetchAssetById] Error:", err?.message || err);
    throw err;
  }
};



/**
 * Updates metadata for an existing asset in Firestore.
 * @param {string} assetId - The ID of the asset to update.
 * @param {Object} updates - The metadata updates to apply.
 */
export const updateAssetMetadata = async (assetId, updates) => {
  try {
    const assetRef = doc(db, 'assets', assetId);

    await updateDoc(assetRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    console.log(`Asset ${assetId} metadata updated successfully.`);
  } catch (error) {
    console.error('Error updating asset metadata:', error);
    throw error;
  }
};

/**
 * Generates a signed URL for direct upload to Firebase Storage.
 * @param {string} fileName - The name of the file.
 * @param {string} organizationId - The organization ID.
 * @param {string} userId - The user ID of the uploader.
 * @returns {string} The signed URL for uploading.
 */
export const generateUploadUrl = async (fileName, organizationId, authToken) => {
  try {
    console.log("[generateUploadUrl] Invoking HTTPS function...");
    const response = await fetch(
      getHttpFunctionUrl("generateUploadUrl"),
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ fileName, organizationId }),
      }
    );

    if (!response.ok) {
      throw new Error(
        `[generateUploadUrl] Failed to generate upload URL. HTTP status: ${response.status}`
      );
    }

    const responseData = await response.json();
    console.log("[generateUploadUrl] Upload URL generated successfully:", responseData);

    return responseData; // Contains uploadUrl and filePath
  } catch (error) {
    console.error("[generateUploadUrl] Error:", error);
    throw error;
  }
};


/**
 * Creates a collection (folder) for organizing assets.
 * @param {string} name - The name of the collection.
 * @param {string} organizationId - The organization ID.
 * @param {string} userId - The user ID of the creator.
 * @param {string|null} parentId - Optional parent collection ID.
 * @param {boolean} isDefault - Whether this is the default folder.
 * @returns {Object} The created collection's metadata.
 */
export const createCollection = async (name, organizationId, userId, parentId = null, isDefault = false) => {
  try {
    const collectionId = uuidv4();

    const collectionData = {
      id: collectionId,
      collectionId, 
      name,
      organizationId,
      createdBy: userId,
      parentId,
      isDefault,
      createdAt: serverTimestamp(),
    };

    await setDoc(doc(db, 'assetCollections', collectionId), collectionData);

    console.log(`Collection ${name} created successfully.`);
    return collectionData;
  } catch (error) {
    console.error('Error creating collection:', error);
    throw error;
  }
};

// utils/frontend_assetManager.js

export const saveCategoryToFirestore = async ({
  name,
  organizationId,
  userId,
  parentId = null,
  isDefault = false,
}) => {
  if (!name || !organizationId) {
    throw new Error('name and organizationId are required');
  }
  const created = await createCollection(
    name,
    organizationId,
    userId || 'system',
    parentId,
    isDefault
  );

  // normalize in case you ever change sources:
  if (!created.id && created.collectionId) created.id = created.collectionId;
  return created;
};

/**
 * Fetches all collections for a specific organization.
 * @param {string} organizationId - The organization ID.
 * @param {string|null} parentId - Optional parent collection ID.
 * @returns {Array<Object>} List of collections.
 */
export const fetchCollections = async (organizationId, parentId = null) => {
  try {
    let collectionsQuery = query(collection(db, 'assetCollections'), where('organizationId', '==', organizationId));

    if (parentId) {
      collectionsQuery = query(collectionsQuery, where('parentId', '==', parentId));
    }

    const querySnapshot = await getDocs(collectionsQuery);
    const collections = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    console.log(`Fetched ${collections.length} collections for organization ${organizationId}`);
    return collections;
  } catch (error) {
    console.error('Error fetching collections:', error);
    throw error;
  }
};

 export const getSignedUploadUrl = async (
   fileName,
   organizationId,
   authToken,
   mimeType
 ) => {
  try {
    const response = await fetch(
      getHttpFunctionUrl("generateUploadUrl"),
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
       body: JSON.stringify({ fileName, organizationId, mimeType }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to generate signed URL for upload.");
    }

    const { uploadUrl, filePath } = await response.json();
    return { uploadUrl, filePath };
  } catch (error) {
    console.error("[getSignedUploadUrl] Error:", error);
    throw error;
  }
};

export {
  ensureDefaultFolder,
  saveMetadataToFirestore,
};

// ────────────────────────────────────────────────────────────────
// helper: append this formId everywhere it belongs on an asset doc
// ────────────────────────────────────────────────────────────────
export const tagAssetWithForm = async (assetId, formId) => {
  if (!assetId || !formId) return;                 // guard

  const assetRef = doc(db, 'assets', assetId);

  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(assetRef);         // atomic read
      if (!snap.exists()) {
        console.warn('[tagAssetWithForm] Asset not found:', assetId);
        return; // end transaction: nothing to update
      }

      const data = snap.data() || {};
      const { versions = {} } = data;              // all version keys

      const updates = {
        formIds: arrayUnion(formId),               // root-level array
        searchKeys: arrayUnion(`form:${formId}`),
      };

      // build “versions.<key>.formIds” paths dynamically
      Object.keys(versions).forEach((k) => {
        updates[`versions.${k}.formIds`] = arrayUnion(formId);
      });

      console.log('[tagAssetWithForm] Tagging asset with form (txn):', {
        assetId,
        formId,
        versionKeys: Object.keys(versions || {}).length,
      });

      tx.update(assetRef, updates);                // atomic update
    });
  } catch (err) {
    // If the asset was deleted between UI paint and click, txn.update can fail.
    console.warn('[tagAssetWithForm] Transaction failed (non-fatal):', {
      assetId,
      msg: err?.message || String(err),
    });
  }
};

/* ────────────────────────────────────────────────────────────────
 * ENTERPRISE: AUF ingest audit trail (non-blocking)
 * ----------------------------------------------------------------
 * Stores an append-only record for asset ingest events so AUF can have
 * a “history” view without forcing formSubmissions writes.
 *
 * Collection: assetIngestEvents
 * Doc shape is intentionally flexible / additive.
 * ---------------------------------------------------------------- */
export const recordAssetIngestEvent = async ({
  assetId,
  organizationId,
  aufFormId,              // the AUF formId where ingest occurred
  originFormId = null,    // Flow-3 origin form if present
  originAttachUfi = null, // Flow-3 origin attach UFI if present
  mode = 'auf',           // 'auf' | 'flow3' | 'navbar' | 'standalone'
  ingestSubmissionId = null,
  userId = null,
  submitterSessionId = null,
  canonicalName = null,
  assetType = null,
  versionNumber = null,
  status = null,          // 'processing'|'published'|etc (best effort)
  tags = [],
  responses = {},         // AUF cleanedResponses (JSON-safe)
} = {}) => {
  try {
    if (!assetId || !organizationId || !aufFormId) {
      console.warn('[recordAssetIngestEvent] Missing required fields; skipping.', {
        assetId,
        organizationId,
        aufFormId,
      });
      return null;
    }

    const payload = {
      assetId,
      organizationId,
      aufFormId,
      originFormId: originFormId || null,
      originAttachUfi: originAttachUfi || null,
      mode,
      ingestSubmissionId: ingestSubmissionId || null,
      userId: userId || null,
      submitterSessionId: submitterSessionId || null,

      canonicalName: canonicalName || null,
      assetType: assetType ?? null,
      versionNumber: (typeof versionNumber === 'number') ? versionNumber : null,
      status: status || null,

      tags: Array.isArray(tags) ? tags : [],
      responses: (responses && typeof responses === 'object') ? responses : {},

      createdAt: serverTimestamp(),
    };

    const ref = await addDoc(collection(db, 'assetIngestEvents'), payload);

    console.log('[recordAssetIngestEvent] wrote assetIngestEvents row:', {
      id: ref.id,
      assetId,
      organizationId,
      aufFormId,
      mode,
    });

    return { id: ref.id };
  } catch (e) {
    console.warn('[recordAssetIngestEvent] failed (non-fatal):', e?.message || e);
    return null;
  }
};

/* ──────────────────────────────────────────────────────────────── */